/**
 * The hero banner kit — the citizen's Home score card, every hero page's
 * banner and the company's Sponsored drawer are built from these, so they
 * share one look: a ring with its figure, a bold title, chips, glass stat
 * tiles and a breakdown strip.
 *
 * Everything is laid out by the CARD's width, never the screen's (a sidebar or
 * a drawer decides how much room there is): the band is a named container
 * (`/hero`) and its words column another (`/hero-content`), and every piece
 * reads the one it sits in. Column classes are static — Tailwind never
 * generates a class built at runtime (`grid-cols-${n}`).
 *
 * Two densities: `default` (a page's own banner) and `compact` (a banner inside
 * a working surface — the company's talent drawer — where what sits below it
 * matters as much: a smaller ring, type and tiles). The band sets it once;
 * every piece reads it.
 *
 * Motion is CSS only, once, on arrival — and none for reduced motion.
 */
import type { LucideIcon } from '@tonnie/assets';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { Card, CardContent } from '../primitives/card';
import { cn } from '../lib/utils';
import { usePrefersReducedMotion } from '../lib/use-prefers-reduced-motion';
import { BrandWatermark } from './brand-watermark';

export type HeroDensity = 'default' | 'compact';

/** Every size the density changes, in one place. */
const DENSITY = {
  default: {
    band: 'p-6 @3xl/hero:p-8',
    grid: 'gap-6 @3xl/hero:gap-10',
    // Where the ring moves beside the words, and the aside beside both.
    cols: '@3xl/hero:grid-cols-[auto_minmax(0,1fr)]',
    colsAside: '@3xl/hero:grid-cols-[auto_minmax(0,1fr)_auto]',
    asideSpan: '',
    // From that same width, the words read left-aligned.
    textStart: 'text-center @3xl/hero:text-left',
    justifyStart: 'justify-center @3xl/hero:justify-start',
    edgeStart: 'mx-auto @3xl/hero:mx-0',
    ring: 156,
    stroke: 12,
    medallion: 'h-14 w-14',
    figure: 'text-4xl',
    figureShort: 'text-5xl',
    caption: 'mt-1.5 text-xs',
    title: 'text-3xl @3xl/hero:text-4xl',
    subtitle: 'mt-2 text-sm @3xl/hero:text-base',
    chips: 'mt-3',
    chip: 'px-3 py-1.5 text-sm',
    tiles: 'mt-5 gap-3',
    tile: 'gap-3 px-3.5 py-3',
    tileIcon: 'h-10 w-10',
    tileGlyph: 'h-5 w-5',
    tileValue: 'text-xl',
  },
  compact: {
    band: 'p-5 @3xl/hero:p-6',
    grid: 'gap-x-6 gap-y-4',
    // A drawer is narrow: the ring sits beside the words from a small width
    // (a stacked ring made the card tall), the aside below both until there
    // is room for a third column.
    cols: '@md/hero:grid-cols-[auto_minmax(0,1fr)]',
    colsAside:
      '@md/hero:grid-cols-[auto_minmax(0,1fr)] @4xl/hero:grid-cols-[auto_minmax(0,1fr)_auto]',
    asideSpan: '@md/hero:col-span-2 @4xl/hero:col-span-1',
    textStart: 'text-center @md/hero:text-left',
    justifyStart: 'justify-center @md/hero:justify-start',
    edgeStart: 'mx-auto @md/hero:mx-0',
    ring: 104,
    stroke: 8,
    // Small and tucked in, clear of the ring's caption.
    medallion: 'h-8 w-8',
    figure: 'text-3xl',
    figureShort: 'text-4xl',
    caption: 'mt-1 text-2xs',
    title: 'text-xl @3xl/hero:text-2xl',
    subtitle: 'mt-1 text-sm',
    chips: 'mt-2.5',
    chip: 'px-2.5 py-1 text-xs',
    tiles: 'mt-3 gap-2',
    tile: 'px-3 py-2',
    tileIcon: '',
    tileGlyph: 'h-3.5 w-3.5',
    tileValue: 'text-base',
  },
} as const satisfies Record<HeroDensity, Record<string, string | number>>;

const HeroDensityContext = createContext<HeroDensity>('default');

function useDensity() {
  return DENSITY[useContext(HeroDensityContext)];
}

/** True from the second frame on — what lets a transition run from 0 on arrival. */
function useArrived(): boolean {
  const reduceMotion = usePrefersReducedMotion();
  const [arrived, setArrived] = useState(reduceMotion);
  useEffect(() => {
    if (reduceMotion) return setArrived(true);
    const frame = requestAnimationFrame(() => setArrived(true));
    return () => cancelAnimationFrame(frame);
  }, [reduceMotion]);
  return arrived;
}

// ── The band ──────────────────────────────────────────────────────────────

interface HeroBandProps {
  /** The band's surface — a hero's gradient or Home's purple (token classes). */
  surfaceClassName: string;
  /** The card's border (a token class). */
  borderClassName?: string;
  /** How am I doing: a `HeroRing`. */
  ring: ReactNode;
  /** What it means: the eyebrow, the title, the chips and the stat tiles. */
  children: ReactNode;
  /** What next: a panel on the right when there is room, under the words when not. */
  aside?: ReactNode;
  /** The breakdown strip under the band (`HeroBreakdown`). */
  footer?: ReactNode;
  className?: string;
  /** Card-level attributes a page's tests read (`data-testid`, `data-*`). */
  cardProps?: Record<`data-${string}`, string>;
  /** `compact` inside a working surface (a drawer); `default` on a page. */
  density?: HeroDensity;
}

/**
 * One card: a band in the hero's colours carrying the brand mark and a soft
 * light from the top corner, and an optional breakdown strip under it. The
 * ring, the words and the aside sit in three columns when there is room and
 * stack, centred, when there is not.
 */
export function HeroBand({
  surfaceClassName,
  borderClassName = 'border-border/60',
  ring,
  children,
  aside,
  footer,
  className,
  cardProps,
  density = 'default',
}: HeroBandProps) {
  const d = DENSITY[density];
  return (
    <HeroDensityContext.Provider value={density}>
      <Card className={cn('overflow-hidden', borderClassName, className)} {...cardProps}>
        <div className={cn('@container/hero relative overflow-hidden', d.band, surfaceClassName)}>
          <BrandWatermark />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -left-24 -top-28 h-80 w-80 rounded-full bg-white/10 blur-3xl"
          />
          <div className={cn('relative grid items-center', d.grid, aside ? d.colsAside : d.cols)}>
            <div className="flex justify-center">{ring}</div>
            {/* The words' own container: the tiles size to this column, not
                to the band (in a drawer the column is far narrower). */}
            <div className={cn('@container/hero-content min-w-0', d.textStart)}>{children}</div>
            {aside ? <div className={d.asideSpan}>{aside}</div> : null}
          </div>
        </div>
        {footer}
      </Card>
    </HeroDensityContext.Provider>
  );
}

/**
 * The band's aside — a glass panel beside the words: an eyebrow that says what
 * it is for ("Next step", "In Portugal"), its one action, and an optional
 * caption under it. A hero start card's next step and InOut's switch are both
 * this panel.
 */
export function HeroAside({
  eyebrow,
  caption,
  children,
  testId,
  dataAttrs,
}: {
  eyebrow: ReactNode;
  caption?: ReactNode;
  children: ReactNode;
  testId?: string;
  dataAttrs?: Record<`data-${string}`, string | number>;
}) {
  return (
    <div
      className="rounded-2xl bg-white/10 p-5 ring-1 ring-white/15 backdrop-blur-sm @3xl/hero:w-72"
      data-testid={testId}
      {...dataAttrs}
    >
      <p className="mb-3 text-center text-2xs font-bold uppercase tracking-widest text-white/90 @3xl/hero:text-left">
        {eyebrow}
      </p>
      {children}
      {caption ? (
        <p className="mt-3 text-center text-xs text-white/90 @3xl/hero:text-left">{caption}</p>
      ) : null}
    </div>
  );
}

/** The eyebrow row above the title: a level or phase pill and its context. */
export function HeroEyebrow({ children }: { children: ReactNode }) {
  const d = useDensity();
  return (
    <div className={cn('mb-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5', d.justifyStart)}>
      {children}
    </div>
  );
}

/** A pill on the band — a level, a phase, a state. */
export function HeroPill({
  children,
  testId,
  dataAttrs,
}: {
  children: ReactNode;
  testId?: string;
  dataAttrs?: Record<`data-${string}`, string | number>;
}) {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-pill bg-white/15 px-3 py-1 text-xs font-bold text-white ring-1 ring-white/20 backdrop-blur-sm"
      data-testid={testId}
      {...dataAttrs}
    >
      {children}
    </span>
  );
}

/**
 * The band's title — wrapping as long as it needs, balanced, never cut (an
 * answer cut by an ellipsis is no answer: InOut's "You can be away from
 * Portugal for up to 183 days in one trip" on a phone) — and the line under it.
 */
export function HeroHeading({
  title,
  subtitle,
  as: Tag = 'h2',
  testId,
  subtitleTestId,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  as?: 'h1' | 'h2' | 'h3';
  testId?: string;
  subtitleTestId?: string;
}) {
  const d = useDensity();
  return (
    <>
      <Tag
        className={cn('font-extrabold tracking-tight text-balance text-white', d.title)}
        data-testid={testId}
      >
        {title}
      </Tag>
      {subtitle && (
        <p
          className={cn('max-w-2xl text-white/90', d.edgeStart, d.subtitle)}
          data-testid={subtitleTestId}
        >
          {subtitle}
        </p>
      )}
    </>
  );
}

// ── The ring ──────────────────────────────────────────────────────────────

/**
 * How am I doing, at a glance: a ring that fills to `progress` (0–100) once,
 * a soft glow behind it, the figure in its centre and an optional medallion on
 * its edge (a badge, a hero's mark). The arc is white unless the caller gives
 * it a tone; on a deep surface the caller scopes the dark palette (`.dark`)
 * around a toned ring.
 */
export function HeroRing({
  progress,
  arcClassName = 'stroke-white/90',
  medallion,
  children,
}: {
  progress: number;
  /** The arc's stroke — a token class. */
  arcClassName?: string;
  /** Sits on the ring's lower-right edge; an `<img>` or an icon, sized here. */
  medallion?: ReactNode;
  /** The figure in the centre (`HeroRingFigure`). */
  children: ReactNode;
}) {
  const arrived = useArrived();
  const d = useDensity();
  const size = d.ring;
  const stroke = d.stroke;
  const r = (size - stroke) / 2;
  const c = r * 2 * Math.PI;
  const offset = c - (Math.min(100, Math.max(0, progress)) / 100) * c;

  return (
    <div className="relative">
      <div aria-hidden="true" className="absolute inset-4 rounded-full bg-white/15 blur-2xl" />
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="-rotate-90" width={size} height={size} aria-hidden="true">
          <circle
            className="stroke-white/15"
            strokeWidth={stroke}
            fill="transparent"
            r={r}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            className={cn(
              arcClassName,
              'transition-[stroke-dashoffset] duration-1000 ease-out motion-reduce:transition-none'
            )}
            strokeWidth={stroke}
            strokeDasharray={c}
            strokeDashoffset={arrived ? offset : c}
            strokeLinecap="round"
            fill="transparent"
            r={r}
            cx={size / 2}
            cy={size / 2}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">{children}</div>
      </div>
      {medallion && (
        <span
          className={cn(
            'absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-white/15 shadow-lg ring-2 ring-white/30 backdrop-blur-md [&>img]:h-4/5 [&>img]:w-4/5 [&>svg]:h-1/2 [&>svg]:w-1/2',
            d.medallion
          )}
        >
          {medallion}
        </span>
      )}
    </div>
  );
}

/**
 * The ring's centre: the figure, large, and what it counts. A short figure (a
 * score) reads larger than a percentage, which needs the room.
 */
export function HeroRingFigure({
  value,
  caption,
  testId,
  short = false,
}: {
  value: ReactNode;
  /** What the figure counts; a figure that speaks for itself has none. */
  caption?: ReactNode;
  testId?: string;
  short?: boolean;
}) {
  const d = useDensity();
  return (
    <>
      <span
        className={cn(
          'font-extrabold leading-none tracking-tight text-white tabular-nums',
          short ? d.figureShort : d.figure
        )}
        data-testid={testId}
      >
        {value}
      </span>
      {caption ? (
        <span
          className={cn(
            'max-w-[7rem] text-center font-medium leading-tight text-white/90',
            d.caption
          )}
        >
          {caption}
        </span>
      ) : null}
    </>
  );
}

// ── Chips and facts ───────────────────────────────────────────────────────

/** A short line of meaning, as a chip — a strongest foundation, a focus area. */
export function HeroChip({
  icon: Icon,
  title,
  children,
}: {
  icon?: LucideIcon;
  /** The chip's tooltip — an exact figure behind a qualitative word. */
  title?: string;
  children: ReactNode;
}) {
  const d = useDensity();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill bg-white/10 font-medium text-white ring-1 ring-white/15',
        d.chip
      )}
      title={title}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-white/85" aria-hidden="true" />}
      {children}
    </span>
  );
}

/** The chips' row, centred when the band stacks. */
export function HeroChips({ children, testId }: { children: ReactNode; testId?: string }) {
  const d = useDensity();
  return (
    <div className={cn('flex flex-wrap gap-2', d.justifyStart, d.chips)} data-testid={testId}>
      {children}
    </div>
  );
}

/**
 * One fact, on a glass tile: an icon, the figure and what it counts. Compact,
 * the icon sits small beside the label and the tile is a light chip — inside a
 * drawer five facts share a column a few hundred pixels wide.
 */
export function HeroStatTile({
  icon: Icon,
  label,
  testId,
  valueTestId,
  children,
}: {
  icon: LucideIcon;
  label: string;
  testId?: string;
  /** On the figure itself — what a test reads as the value. */
  valueTestId?: string;
  children: ReactNode;
}) {
  const density = useContext(HeroDensityContext);
  const d = DENSITY[density];
  const value = (
    <span
      className={cn('block font-bold leading-tight text-white tabular-nums', d.tileValue)}
      data-testid={valueTestId}
    >
      {children}
    </span>
  );

  if (density === 'compact') {
    return (
      <div
        className={cn('min-w-0 rounded-lg bg-white/10 text-left ring-1 ring-white/10', d.tile)}
        data-testid={testId}
      >
        {value}
        {/* A label wraps to a second line rather than "Citizen l…". */}
        <span className="mt-0.5 flex min-w-0 items-start gap-1 text-2xs font-medium leading-tight text-white/90">
          <Icon className={cn('mt-px shrink-0 text-white/90', d.tileGlyph)} aria-hidden="true" />
          <span className="min-w-0 break-words">{label}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex min-w-0 items-center rounded-xl bg-white/10 text-left ring-1 ring-white/10',
        d.tile
      )}
      data-testid={testId}
    >
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-pill bg-white/15',
          d.tileIcon
        )}
      >
        <Icon className={cn('text-white', d.tileGlyph)} aria-hidden="true" />
      </span>
      <span className="min-w-0">
        {value}
        <span className="block truncate text-2xs font-medium text-white/90">{label}</span>
      </span>
    </div>
  );
}

/**
 * The tiles' grid, by the WORDS COLUMN's width (never the band's): one to a
 * row only on a column narrower than a phone's (24rem — a band inside a
 * partner's modal or an app sheet, where two would clip a date), two to a row
 * from there, all of them in one row once each has room — four stay two by
 * two until then, never three and one.
 * Two tiles are a compact pair. Compact tiles are light chips, so they fit
 * more to a row: three, then all five, in a drawer's column.
 */
const TILE_COLUMNS = {
  default: {
    1: 'max-w-md grid-cols-1',
    2: 'max-w-md grid-cols-1 @sm/hero-content:grid-cols-2',
    3: 'max-w-4xl grid-cols-1 @sm/hero-content:grid-cols-2 @lg/hero-content:grid-cols-3',
    4: 'max-w-4xl grid-cols-1 @sm/hero-content:grid-cols-2 @2xl/hero-content:grid-cols-4',
    5: 'max-w-5xl grid-cols-1 @sm/hero-content:grid-cols-2 @lg/hero-content:grid-cols-3 @4xl/hero-content:grid-cols-5',
  },
  compact: {
    1: 'max-w-xs grid-cols-1',
    2: 'max-w-sm grid-cols-2',
    3: 'max-w-xl grid-cols-3',
    4: 'max-w-2xl grid-cols-2 @sm/hero-content:grid-cols-4',
    5: 'max-w-3xl grid-cols-3 @md/hero-content:grid-cols-5',
  },
} as const;

export function HeroStatTiles({
  count,
  children,
}: {
  count: keyof (typeof TILE_COLUMNS)['default'];
  children: ReactNode;
}) {
  const density = useContext(HeroDensityContext);
  const d = DENSITY[density];
  return (
    <div className={cn('grid', d.edgeStart, d.tiles, TILE_COLUMNS[density][count])}>{children}</div>
  );
}

// ── The breakdown strip ───────────────────────────────────────────────────

const BREAKDOWN_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 @xl:grid-cols-3',
  4: 'grid-cols-2 @3xl:grid-cols-4',
  5: 'grid-cols-2 @xl:grid-cols-3 @3xl:grid-cols-5',
};

/**
 * The strip under a band: one item per part of the hero (Home's heroes, a
 * hero's tracks, worlds, pillars or rarities) — as many columns as items (up
 * to five) when they fit, fewer when they do not.
 */
export function HeroBreakdown({ count, children }: { count: number; children: ReactNode }) {
  return (
    <CardContent className="@container bg-background p-4">
      <div
        className={cn('grid gap-x-6 gap-y-4', BREAKDOWN_COLUMNS[Math.min(Math.max(count, 1), 5)])}
      >
        {children}
      </div>
    </CardContent>
  );
}

interface HeroBreakdownItemProps {
  label: ReactNode;
  /** The figure on the right — a status, a fraction, a percentage. */
  value: ReactNode;
  /** 0–100, the bar's fill. */
  progress: number;
  /** Before the name: an icon, an emoji, an image. */
  leading?: ReactNode;
  /** The bar's track and fill (token classes). */
  trackClassName: string;
  fillClassName?: string;
  /** A fill the caller only knows as a colour (a pillar's own). */
  fillStyle?: CSSProperties;
  valueClassName?: string;
  valueStyle?: CSSProperties;
  testId?: string;
}

/**
 * One item: its name and figure on one line, its bar under them. To make it a
 * link or a button, the caller wraps it (a router link, a tab switch) — this
 * package draws, it does not route.
 */
export function HeroBreakdownItem({
  label,
  value,
  progress,
  leading,
  trackClassName,
  fillClassName,
  fillStyle,
  valueClassName = 'text-muted-foreground',
  valueStyle,
  testId,
}: HeroBreakdownItemProps) {
  const arrived = useArrived();
  const pct = Math.min(100, Math.max(0, progress));
  return (
    <div className="group min-w-0" data-testid={testId} data-progress={progress}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          {leading}
          <span className="truncate text-xs font-medium text-muted-foreground transition-colors group-hover:text-foreground">
            {label}
          </span>
        </span>
        <span
          className={cn('shrink-0 whitespace-nowrap text-xs font-semibold', valueClassName)}
          style={valueStyle}
        >
          {value}
        </span>
      </div>
      <div className={cn('h-1.5 overflow-hidden rounded-pill', trackClassName)}>
        <div
          className={cn(
            'h-full rounded-pill transition-[width] delay-300 duration-700 ease-out motion-reduce:transition-none',
            fillClassName
          )}
          style={{ ...fillStyle, width: `${arrived ? pct : 0}%` }}
        />
      </div>
    </div>
  );
}
